import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2Icon } from 'lucide-react';
import { useEffect } from 'react';
import { useForm, type Control } from 'react-hook-form';
import type { z } from 'zod';
import type { AdminUserQuotaDetailDTO } from '@shared/schemas/quota';
import { adminQuotaConfigSchema } from '@shared/schemas/quota';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { FieldError } from '@/components/ui/field';
import { QuotaConfigFields } from '@/features/quota/components/QuotaConfigFields';
import { getDefaultQuotaRenewsAtIso } from '@/features/quota/quota-format';
import { getQuotaErrorMessage } from '@/features/quota/quota-errors';
import { useConfigureQuotaMutation } from '@/features/quota/useAdminUserQuota';
import { toast } from 'sonner';

type ConfigFormInput = z.input<typeof adminQuotaConfigSchema>;
type ConfigFormOutput = z.output<typeof adminQuotaConfigSchema>;

type AdminUserQuotaConfigFormProps = {
  detail: AdminUserQuotaDetailDTO;
  userId: string;
};

export function AdminUserQuotaConfigForm({ detail, userId }: AdminUserQuotaConfigFormProps) {
  const configureMutation = useConfigureQuotaMutation(userId);
  const form = useForm<ConfigFormInput, undefined, ConfigFormOutput>({
    defaultValues: {
      enforcementEnabled: detail.config?.enforcementEnabled ?? true,
      renewalIntervalDays: detail.config?.renewalIntervalDays ?? detail.status.renewalIntervalDays ?? 30,
      renewsAt: detail.config?.renewsAt ?? detail.status.renewsAt ?? getDefaultQuotaRenewsAtIso(),
      tokenLimit: detail.config?.tokenLimit ?? detail.status.tokenLimit ?? 1_000_000,
    },
    resolver: zodResolver(adminQuotaConfigSchema),
  });

  useEffect(() => {
    form.reset({
      enforcementEnabled: detail.config?.enforcementEnabled ?? true,
      renewalIntervalDays: detail.config?.renewalIntervalDays ?? detail.status.renewalIntervalDays ?? 30,
      renewsAt: detail.config?.renewsAt ?? detail.status.renewsAt ?? getDefaultQuotaRenewsAtIso(),
      tokenLimit: detail.config?.tokenLimit ?? detail.status.tokenLimit ?? 1_000_000,
    });
  }, [detail, form]);

  async function handleSubmit(values: ConfigFormOutput) {
    form.clearErrors('root');

    try {
      await configureMutation.mutateAsync(values);
      toast.success('Configuration du quota enregistrée.');
    } catch (error) {
      const message = getQuotaErrorMessage(error);
      form.setError('root', { message });
    }
  }

  if (detail.user.accountType === 'subscriber') {
    return (
      <Card className="rounded-[var(--radius-surface-4xl)] border-border/70">
        <CardHeader>
          <CardTitle>Configuration du quota</CardTitle>
          <CardDescription>
            Les comptes abonnés ne sont pas soumis à une limite de tokens en v1.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <Card className="rounded-[var(--radius-surface-4xl)] border-border/70">
      <CardHeader>
        <CardTitle>Configuration du quota</CardTitle>
        <CardDescription>
          Limite, renouvellement et enforcement pour ce compte démo.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form className="space-y-5" onSubmit={form.handleSubmit(handleSubmit)}>
          <QuotaConfigFields control={form.control as unknown as Control<Record<string, unknown>>} disabled={configureMutation.isPending} />
          <FieldError errors={[form.formState.errors.root]} />
          <div className="flex justify-end">
            <Button
              type="submit"
              data-testid="quota-config-submit"
              disabled={configureMutation.isPending}
              className="h-11 px-6"
            >
              {configureMutation.isPending ? <Loader2Icon className="animate-spin" /> : null}
              Enregistrer la configuration
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
